import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useConsignees,
  useCreateConsignee,
  buildInviteLink,
} from '../../hooks/useConsignees'
import { useSales } from '../../hooks/useSales'
import { computeUnsettledTotals } from '../../hooks/useConsigneeSettlements'
import { BackLink } from '../../components/BackLink'
import { Card, Skeleton, Chip } from '../../components/ui'
import { formatMoney } from '../../lib/format'

export default function ConsigneesPage() {
  const { data: consignees, isLoading } = useConsignees()
  const { data: sales } = useSales()
  const create = useCreateConsignee()

  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [cut, setCut] = useState('25')
  const [error, setError] = useState('')
  const [createdInviteLink, setCreatedInviteLink] = useState<string | null>(null)
  const [createdConsigneeName, setCreatedConsigneeName] = useState('')
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle')

  const reset = () => {
    setName('')
    setEmail('')
    setCut('25')
    setError('')
    setAdding(false)
    setCreatedInviteLink(null)
    setCreatedConsigneeName('')
    setCopyState('idle')
  }

  const handleCreate = async () => {
    setError('')
    if (!name.trim() || !email.trim()) {
      setError('Name and email are both required.')
      return
    }
    try {
      const created = await create.mutateAsync({
        name: name.trim(),
        invite_email: email.trim().toLowerCase(),
        cut_pct: Number(cut) || 25,
      })
      setCreatedInviteLink(buildInviteLink(created.invite_token))
      setCreatedConsigneeName(created.name)
      // Keep the form values visible while showing the link.
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const handleCopy = async () => {
    if (!createdInviteLink) return
    try {
      await navigator.clipboard.writeText(createdInviteLink)
      setCopyState('copied')
      window.setTimeout(() => setCopyState('idle'), 2000)
    } catch {
      // Older browsers / no clipboard permission
    }
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 640 }}>
      <BackLink fallback="/settings" />
      <h1 style={{ marginBottom: 'var(--space-2)' }}>Consignees</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
        People who sell your prints on your behalf — mom at a flea market, a friend at a craft fair,
        a cousin&apos;s storefront. Each one signs in with their own account, sees only what they&apos;re
        holding, logs sales from their phone, and you settle up periodically.
      </p>

      {isLoading ? (
        <Skeleton height={60} />
      ) : (
        <>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 'var(--space-4)' }}>
            {(consignees ?? []).map((c) => {
              const status = c.revoked_at
                ? { label: 'Revoked', tone: 'danger' as const }
                : c.accepted_at
                  ? { label: 'Active', tone: 'success' as const }
                  : { label: 'Pending invite', tone: 'warning' as const }
              const totals = computeUnsettledTotals(sales ?? [], c.id)
              return (
                <li key={c.id} style={{ marginBottom: 'var(--space-2)' }}>
                  <Link
                    to={`/settings/consignees/${c.id}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                  >
                    <Card padding="var(--space-3)">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600 }}>{c.name}</span>
                            <Chip tone={status.tone}>{status.label}</Chip>
                          </div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                            {c.invite_email} · cut {c.cut_pct}%
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: 'var(--font-size-xs)' }}>
                          {totals.count > 0 ? (
                            <>
                              <div style={{ color: 'var(--color-success-text)', fontWeight: 700 }}>
                                {formatMoney(totals.ownerReceives)} owed
                              </div>
                              <div style={{ color: 'var(--color-text-faint)' }}>
                                {totals.count} unsettled sale{totals.count === 1 ? '' : 's'}
                              </div>
                            </>
                          ) : (
                            <div style={{ color: 'var(--color-text-faint)' }}>settled up</div>
                          )}
                        </div>
                      </div>
                    </Card>
                  </Link>
                </li>
              )
            })}
          </ul>

          {createdInviteLink ? (
            <Card style={{ marginBottom: 'var(--space-3)' }}>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, marginBottom: 'var(--space-2)' }}>
                ✓ {createdConsigneeName} added — share this link
              </div>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
                Text or email this URL to them. Opening it lets them sign in with Google and bind
                their account to your shop. The link is single-use.
              </p>
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'stretch' }}>
                <input
                  readOnly
                  value={createdInviteLink}
                  style={{
                    flex: 1,
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface-2)',
                    color: 'var(--color-text)',
                    fontSize: 'var(--font-size-xs)',
                    fontFamily: 'var(--font-mono)',
                  }}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    background: 'var(--color-primary)',
                    color: 'white',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: 600,
                    fontSize: 'var(--font-size-xs)',
                  }}
                >
                  {copyState === 'copied' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <button
                type="button"
                onClick={reset}
                style={{ marginTop: 'var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
              >
                Done
              </button>
            </Card>
          ) : adding ? (
            <Card style={{ marginBottom: 'var(--space-3)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <FormField label="Name (display)" value={name} onChange={setName} placeholder="Mom" />
                <FormField
                  label="Their Gmail address"
                  value={email}
                  onChange={setEmail}
                  placeholder="mom@gmail.com"
                  type="email"
                />
                <FormField
                  label="Cut %"
                  value={cut}
                  onChange={setCut}
                  placeholder="25"
                  type="decimal"
                  hint="Percentage of each sale price that goes to them."
                />
                {error && (
                  <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>{error}</p>
                )}
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={create.isPending}
                    style={{
                      flex: 1,
                      padding: 'var(--space-3)',
                      background: 'var(--color-primary)',
                      color: 'white',
                      borderRadius: 'var(--radius-md)',
                      fontWeight: 600,
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    {create.isPending ? 'Creating…' : 'Add consignee'}
                  </button>
                  <button
                    type="button"
                    onClick={reset}
                    style={{ padding: 'var(--space-3)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </Card>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              style={{
                width: '100%',
                padding: 'var(--space-3) var(--space-5)',
                background: 'var(--color-primary)',
                color: 'white',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                fontSize: 'var(--font-size-sm)',
              }}
            >
              + Add consignee
            </button>
          )}
        </>
      )}
    </div>
  )
}

function FormField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  hint,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: 'text' | 'email' | 'decimal'
  hint?: string
}) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
        {label}
      </label>
      <input
        type={type === 'email' ? 'email' : 'text'}
        inputMode={type === 'decimal' ? 'decimal' : undefined}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
        }}
      />
      {hint && (
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>
          {hint}
        </p>
      )}
    </div>
  )
}
